import { createTheme, alpha } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    gold: Palette['primary'];
    crimson: Palette['primary'];
  }
  interface PaletteOptions {
    gold?: PaletteOptions['primary'];
    crimson?: PaletteOptions['primary'];
  }
}

export const LOJ_COLORS = {
  // Deep dungeon blacks
  void:        '#040302',
  abyss:       '#0a0806',
  shadow:      '#110e09',
  darkStone:   '#1a1510',
  stone:       '#221c14',
  midStone:    '#2e2518',
  // Gold spectrum
  goldDim:     '#5c4510',
  goldMuted:   '#7a5e18',
  gold:        '#a67c20',
  goldBright:  '#c4961e',
  goldShine:   '#e0b030',
  goldGlow:    '#f5cc50',
  // Parchment
  parchmentDark:  '#8b6c3c',
  parchment:      '#c4a26a',
  parchmentLight: '#d9bc8e',
  parchmentPale:  '#eddcb8',
  // Accent: crystal blue
  crystalDeep:  '#0d1f3c',
  crystalMid:   '#1a3a6e',
  crystal:      '#2a5caa',
  crystalLight: '#4a82d4',
  crystalGlow:  '#6aaeff',
  // Accent: sand/amber
  sandDeep:    '#3d2008',
  sandMid:     '#7a4010',
  sand:        '#c06820',
  sandLight:   '#e08840',
  sandGlow:    '#f5a840',
  // Red
  bloodDark:   '#3a0808',
  blood:       '#8b1a1a',
  ember:       '#c43030',
};

const lojTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: LOJ_COLORS.goldBright,
      light: LOJ_COLORS.goldGlow,
      dark: LOJ_COLORS.gold,
    },
    secondary: {
      main: LOJ_COLORS.crystalLight,
      light: LOJ_COLORS.crystalGlow,
      dark: LOJ_COLORS.crystal,
    },
    background: {
      default: LOJ_COLORS.abyss,
      paper: LOJ_COLORS.shadow,
    },
    text: {
      primary: LOJ_COLORS.parchmentLight,
      secondary: LOJ_COLORS.parchment,
      disabled: LOJ_COLORS.goldMuted,
    },
    gold: {
      main: LOJ_COLORS.goldBright,
      light: LOJ_COLORS.goldGlow,
      dark: LOJ_COLORS.gold,
    },
    crimson: {
      main: LOJ_COLORS.ember,
      light: LOJ_COLORS.blood,
      dark: LOJ_COLORS.bloodDark,
    },
    divider: alpha(LOJ_COLORS.goldBright, 0.25),
  },
  typography: {
    fontFamily: "'Crimson Text', Georgia, serif",
    h1: {
      fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
      fontWeight: 700,
      letterSpacing: '0.08em',
      color: LOJ_COLORS.goldGlow,
    },
    h2: {
      fontFamily: "'Cinzel', serif",
      fontWeight: 700,
      letterSpacing: '0.06em',
      color: LOJ_COLORS.goldShine,
    },
    h3: {
      fontFamily: "'Cinzel', serif",
      fontWeight: 600,
      letterSpacing: '0.05em',
      color: LOJ_COLORS.goldBright,
    },
    h4: {
      fontFamily: "'Cinzel', serif",
      fontWeight: 600,
      letterSpacing: '0.04em',
      color: LOJ_COLORS.parchmentLight,
    },
    h5: {
      fontFamily: "'Cinzel', serif",
      fontWeight: 400,
      letterSpacing: '0.03em',
    },
    h6: {
      fontFamily: "'Cinzel', serif",
      fontWeight: 400,
    },
    body1: {
      fontFamily: "'Crimson Text', Georgia, serif",
      fontSize: '1.1rem',
      lineHeight: 1.75,
      color: LOJ_COLORS.parchmentLight,
    },
    body2: {
      fontFamily: "'Crimson Text', Georgia, serif",
      fontSize: '1rem',
      lineHeight: 1.65,
      color: LOJ_COLORS.parchment,
    },
    subtitle1: {
      fontFamily: "'Cinzel', serif",
      fontSize: '0.9rem',
      letterSpacing: '0.1em',
      color: LOJ_COLORS.goldBright,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: LOJ_COLORS.abyss,
          color: LOJ_COLORS.parchmentLight,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: "'Cinzel', serif",
          fontWeight: 600,
          letterSpacing: '0.08em',
          fontSize: '0.9rem',
          color: LOJ_COLORS.parchment,
          textTransform: 'none',
          minHeight: 56,
          transition: 'all 0.3s ease',
          '&.Mui-selected': {
            color: LOJ_COLORS.goldGlow,
          },
          '&:hover': {
            color: LOJ_COLORS.parchmentPale,
            backgroundColor: alpha(LOJ_COLORS.goldBright, 0.08),
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: LOJ_COLORS.goldBright,
          height: 3,
          boxShadow: `0 0 10px ${LOJ_COLORS.goldBright}, 0 0 20px ${alpha(LOJ_COLORS.goldBright, 0.5)}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: LOJ_COLORS.shadow,
          border: `1px solid ${alpha(LOJ_COLORS.goldBright, 0.2)}`,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: alpha(LOJ_COLORS.goldBright, 0.25),
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: "'Cinzel', serif",
          fontSize: '0.75rem',
          letterSpacing: '0.05em',
        },
      },
    },
  },
});

export default lojTheme;
